/**
 * Local east/north/up frame ("ENU") anchored at a geographic origin.
 *
 * The world is rendered in metres on a tangent plane, not in degrees. We use a
 * simple equirectangular tangent-plane map:
 *
 *   east  = (lon - lon0) * (pi/180) * R * cos(lat0)
 *   north = (lat - lat0) * (pi/180) * R
 *
 * Why this and not something fancier:
 *  - It is exactly invertible (it is a linear map), so geo -> ENU -> geo is
 *    lossless regardless of distance.
 *  - Meridians and parallels stay axis-aligned, so a Web Mercator tile is an
 *    axis-aligned rectangle in ENU. That keeps chunk transforms to a
 *    translation, with no rotation or shear.
 *  - Unlike rendering in Mercator directly, it has no 1/cos(lat) stretch, so a
 *    metre in the world is a metre on screen.
 *
 * The cost is a scale error that grows with distance from the origin. The east
 * axis uses cos(lat0) rather than cos(lat), so a point dN metres north of the
 * origin is stretched east-west by a factor of
 *
 *   cos(lat0)/cos(lat)  ~=  1 + dN * tan(lat0) / R
 *
 * This is *relative*, not a fixed number of metres: at lat 57.7 it is ~0.25% at
 * 10 km north and ~1.2% at 50 km. It is a smooth global distortion, not a seam —
 * terrain, water and trees are all placed through the same map, so they stay
 * mutually aligned and neighbouring tiles still abut exactly. Nothing looks
 * wrong; the world is just imperceptibly wide in the far north of the loaded
 * area. Rebasing the origin as the player travels keeps the factor small, and
 * rebasing is cheap because it only moves chunk transforms, never vertex data.
 */

import { DEG_TO_RAD, EARTH_RADIUS, RAD_TO_DEG } from './mercator.js';

export interface GeoPoint {
  lat: number;
  lon: number;
}

/** A local frame anchor. Build with `makeOrigin` so `cosLat` stays consistent. */
export interface GeoOrigin {
  readonly lat: number;
  readonly lon: number;
  /** cos(lat0), cached because it is used on every conversion. */
  readonly cosLat: number;
}

/** Planar offset from an origin, in metres. `up` is handled separately. */
export interface EnuPoint {
  east: number;
  north: number;
}

export function makeOrigin(lat: number, lon: number): GeoOrigin {
  return { lat, lon, cosLat: Math.cos(lat * DEG_TO_RAD) };
}

/** Metres per degree of longitude at the origin's latitude. */
export function metresPerDegreeLon(origin: GeoOrigin): number {
  return DEG_TO_RAD * EARTH_RADIUS * origin.cosLat;
}

/** Metres per degree of latitude. Constant on a sphere. */
export function metresPerDegreeLat(): number {
  return DEG_TO_RAD * EARTH_RADIUS;
}

export function geoToEnu(origin: GeoOrigin, lat: number, lon: number): EnuPoint {
  return {
    east: (lon - origin.lon) * metresPerDegreeLon(origin),
    north: (lat - origin.lat) * metresPerDegreeLat(),
  };
}

export function enuToGeo(origin: GeoOrigin, east: number, north: number): GeoPoint {
  return {
    lat: origin.lat + north / metresPerDegreeLat(),
    lon: origin.lon + east / metresPerDegreeLon(origin),
  };
}

/**
 * Where `oldOrigin` sits when expressed in `newOrigin`'s frame.
 *
 * NOTE: this is *not* sufficient to rebase the world. Translating every loaded
 * object by this offset leaves a residual error of
 *
 *   dLat * (pi/180) * R * (cos(lat_old) - cos(lat_new))
 *
 * where `dLat` is the object's own latitude offset from the old origin, because
 * the two frames scale their east axis by the cosine of different latitudes. At
 * a 0.35 degree rebase that is already ~11 m for an object 0.02 degrees north —
 * a visible seam, not a rounding error.
 *
 * So: anything that knows its own geographic position (terrain chunks know
 * their TileKey; the player is stored as lat/lon) must be *re-derived* with
 * `geoToEnu` against the new origin, which is exact. `rebaseOffset` is only for
 * the rare object that has no retained geographic position and is close to the
 * origin — and `rebaseResidual` tells you what that will cost.
 */
export function rebaseOffset(oldOrigin: GeoOrigin, newOrigin: GeoOrigin): EnuPoint {
  return geoToEnu(newOrigin, oldOrigin.lat, oldOrigin.lon);
}

/**
 * The error incurred by translating a point at `lat` from `oldOrigin`'s frame
 * into `newOrigin`'s frame using `rebaseOffset`, in metres. Exposed so callers
 * can assert it is negligible instead of assuming so.
 */
export function rebaseResidual(oldOrigin: GeoOrigin, newOrigin: GeoOrigin, lat: number): number {
  return Math.abs(
    (lat - oldOrigin.lat) * DEG_TO_RAD * EARTH_RADIUS * (oldOrigin.cosLat - newOrigin.cosLat),
  );
}

/** Planar distance in metres between two points in the same frame. */
export function enuDistance(a: EnuPoint, b: EnuPoint): number {
  const de = a.east - b.east;
  const dn = a.north - b.north;
  return Math.hypot(de, dn);
}

/** Convert a compass heading (degrees clockwise from north) to an ENU direction. */
export function headingToEnu(headingDeg: number): EnuPoint {
  const r = headingDeg * DEG_TO_RAD;
  return { east: Math.sin(r), north: Math.cos(r) };
}

/** Inverse of `headingToEnu`, returning degrees clockwise from north in [0, 360). */
export function enuToHeading(east: number, north: number): number {
  const deg = Math.atan2(east, north) * RAD_TO_DEG;
  return (deg + 360) % 360;
}
